<svelte:options
	customElement={{
		tag: 'rf-carrousel',
		shadow: 'open',
	}}
/>

<script module>
	/**
	 * Import
	 */
	import type { CanvasPanelWebComponent } from '@src/types/canvas-panel.d.ts';

	/**
	 * Types
	 */
	type ReframeCarrouselItem = HTMLElement | CanvasPanelWebComponent;

	export interface ReframeCarrouselProps {
		id?: string;
		tag?: string;
		controls?: boolean | 'false';
		bullets?: boolean | 'false';
		autoplay?: boolean;
		speed?: number;
		loop?: boolean;
		'items-per-view'?: number;
		'space-between-items'?: number;
		'animation-type'?: 'slide' | 'fade' | 'page';
		'canvas-fit'?: 'auto' | 'contain' | 'cover';
		previousLabel?: string;
		nextLabel?: string;
		'no-collection'?: boolean;
	}
</script>

<script lang="ts">
	/**
	 * Import
	 */
	import classNames from 'classnames';
	import cssVariables from '@src/utils/cssvariables.ts';
	import parseBool from '@src/utils/bool';
	import { onMount, onDestroy, createEventDispatcher } from 'svelte';
	import { get } from 'svelte/store';
	import { useSlottedElements } from '@src/composables/useSlottedElements';
	import debounced from '@src/utils/debounced';
	import throttled from '@src/utils/throttled';
	import { v4 as uuidv4 } from 'uuid';
	import { useCollection } from '@src/composables/useCollection.svelte.ts';

	const dispatch = createEventDispatcher();

	/**
	 * Props
	 */
	let {
		id = '',
		tag = 'section',
		controls = true,
		bullets = true,
		autoplay = false,
		speed = 5000,
		loop = false,
		'animation-type': animationType = 'slide',
		'space-between-items': spaceBetweenItems = 24,
		'items-per-view': itemsPerView = 3,
		'canvas-fit': canvasFit = 'auto',
		previousLabel = 'Previous',
		nextLabel = 'Next',
		'no-collection': noCollection = false,
	}: ReframeCarrouselProps = $props();

	/**
	 * Constants
	 */
	const name = 'rf-carrousel';
	const componentId = uuidv4();

	/**
	 * Variables
	 */
	let currentIndex: number = $state(0);
	let items: ReframeCarrouselItem[] = $state([]);
	let autoplayInterval: ReturnType<typeof setInterval> | null = null;
	let singleElementWidth: number = $state(0);
	let observer: ResizeObserver | null = null;
	let actualItemsPerView: number = $state(3);
	let touchStartX: number | null = null;
	let touchEndX: number | null = null;
	const { element, getSlottedElements, forEachSlottedElement } = useSlottedElements();

	/**
	 * Derived State
	 */
	const pages: number = $derived(Math.ceil(items.length - actualItemsPerView + 1));
	const isAtStart: boolean = $derived(currentIndex === 0);
	const isAtEnd: boolean = $derived(currentIndex === pages - 1);
	const hasControls: boolean = $derived(controls !== 'false' && controls);
	const hasBullets: boolean = $derived(bullets !== 'false' && bullets);

	/**
	 * Composables
	 */

	export const collection = parseBool(noCollection)
		? undefined
		: useCollection(id, {
				element,
				moduleName: name,
				componentId,
			});

	/**
	 * Local Functions
	 */
	const initCarrousel = (): void => {
		const currentContainer = get(element);
		if (!currentContainer) return;
		dispatch('init');
		/**
		 * Get items per view value from css variable
		 */

		actualItemsPerView =
			animationType === 'fade' || animationType === 'page'
				? 1
				: Math.max(
						1,
						Math.min(items.length, parseFloat(getComputedStyle(currentContainer).getPropertyValue('--rf-carrousel-slides-per-view')))
					);

		/**
		 * Set single element width using the wrapper width and the space between items
		 */
		const wrapperWidth = currentContainer.getBoundingClientRect().width;
		const spaceBetween = parseFloat(getComputedStyle(currentContainer).getPropertyValue('--rf-carrousel-space-between-items'));
		singleElementWidth = (wrapperWidth - spaceBetween * (actualItemsPerView - 1)) / actualItemsPerView;

		/**
		 * Start autoplay if autoplay is enabled and pages > 1
		 */
		if (autoplay && pages > 1) startAutoplay();
	};

	const setItemsClasses = (): void => {
		/**
		 * Get items in view and add classes to them
		 */
		const itemsInView = items.slice(currentIndex, currentIndex + actualItemsPerView);
		items.forEach((item) => {
			item.classList.remove('rf-carrousel-slide--in-view', 'rf-carrousel-slide--previous', 'rf-carrousel-slide--next');
		});

		// Add class to items in view
		itemsInView.forEach((item) => {
			item.classList.add('rf-carrousel-slide--in-view');
		});

		if (animationType === 'page') {
			// Add previous class to all items before the current index
			items.slice(0, currentIndex).forEach((item) => {
				item.classList.add('rf-carrousel-slide--previous');
			});

			// Add next class to all items after the last visible index
			items.slice(currentIndex + actualItemsPerView).forEach((item) => {
				item.classList.add('rf-carrousel-slide--next');
			});
			// If loop add class for seamless feel loop
			if (loop) {
				if (currentIndex + actualItemsPerView === items.length) {
					items[0]?.classList.remove('rf-carrousel-slide--previous');
					items[0]?.classList.add('rf-carrousel-slide--next');
				}
				if (currentIndex === 0) {
					items[items.length - 1]?.classList.remove('rf-carrousel-slide--next');
					items[items.length - 1]?.classList.add('rf-carrousel-slide--previous');
				}
			}
		} else {
			// Add class to previous item if it exists
			if (currentIndex > 0) {
				items[currentIndex - 1]?.classList.add('rf-carrousel-slide--previous');
			}

			// Add class to next item if it exists
			if (currentIndex + actualItemsPerView < items.length) {
				items[currentIndex + actualItemsPerView]?.classList.add('rf-carrousel-slide--next');
			}
		}
	};

	const handleTouchStart = (event: TouchEvent) => {
		touchStartX = event.touches[0].clientX;
	};

	const handleTouchEnd = (event: TouchEvent) => {
		if (touchStartX === null) return;
		touchEndX = event.changedTouches[0].clientX;

		const deltaX = touchEndX - touchStartX;

		// Minimum swipe distance to consider it a swipe
		const minSwipeDistance = 50;

		if (deltaX > minSwipeDistance) {
			toPreviousPage(); // Swipe right
		} else if (deltaX < -minSwipeDistance) {
			toNextPage(); // Swipe left
		}

		// Reset values
		touchStartX = null;
		touchEndX = null;
	};

	/**
	 * Exported functions
	 */

	export const toNextPage = (): void => {
		/**
		 * Function to set page to next index
		 */
		let newIndex = currentIndex;
		if (currentIndex < pages - 1) {
			newIndex += 1;
		} else if (loop && currentIndex === pages - 1) {
			newIndex = 0;
		}
		dispatch('next', { index: newIndex });
		setPage(newIndex);
	};

	export const toPreviousPage = (): void => {
		/**
		 * Function to set page to previous index
		 */
		let newIndex = currentIndex;
		if (currentIndex > 0) {
			newIndex -= 1;
		} else if (loop && currentIndex === 0) {
			newIndex = pages - 1;
		}
		dispatch('previous', { index: newIndex });
		setPage(newIndex);
	};

	const throttledToNextPage = throttled(toNextPage, 500);
	const throttledToPreviousPage = throttled(toPreviousPage, 500);

	export const setPage = (index?: number): void => {
		/**
		 * Function to set current page to specific index
		 */
		if (autoplay && autoplayInterval) stopAutoplay();
		// TODO: check if index is out of bounds (add notification)
		if (index && index < 0) index = 0;
		if (index && index > pages - 1) index = pages - 1;
		if (index !== undefined) currentIndex = index;
		dispatch('setPage', { index: currentIndex });
		if (autoplay) startAutoplay();
	};

	export const startAutoplay = (): void => {
		if (autoplay) {
			dispatch('startAutoplay');
			autoplayInterval = setInterval(() => {
				toNextPage();
			}, speed);
		}
	};

	export const stopAutoplay = (): void => {
		if (autoplayInterval) {
			dispatch('stopAutoplay');
			clearInterval(autoplayInterval);
		}
	};

	// Resize carrousel
	const carrouselResize = debounced(initCarrousel, 100);

	/**
	 * Effects
	 */
	$effect(() => {
		/**
		 * If current index is at the end of the pages and loop is false, stop autoplay
		 */
		if (isAtEnd && !loop) stopAutoplay();
		/**
		 * Set items classes when the carrousel index changes
		 */
		setItemsClasses();
		/**
		 * If current index is at the end of the pages, set it to the last page
		 * This is to prevent misIndexes when the carrousel is at the end but the items per view is changed
		 */
		if (currentIndex + 1 > pages) {
			setPage(pages);
		}
	});
	/**
	 * Lifecycle
	 */
	onMount(() => {
		const currentContainer = get(element);
		if (!currentContainer) return;
		items = getSlottedElements() as ReframeCarrouselItem[];
		setItemsClasses();
		/**
		 * Set canvas attributes
		 */
		forEachSlottedElement((el) => {
			if (el.firstElementChild instanceof HTMLElement && el.firstElementChild.tagName === 'RF-CANVAS') {
				el.firstElementChild.setAttribute('fit', animationType === 'page' ? 'contain' : canvasFit);
				el.firstElementChild.setAttribute('interactive', 'keydown');
			}
		});
		/**
		 * add resize observer
		 */
		observer = new ResizeObserver(() => {
			if (autoplay) stopAutoplay();
			carrouselResize();
			dispatch('resize');
		});
		observer.observe(currentContainer);
	});

	onDestroy(() => {
		if (autoplay && autoplayInterval) stopAutoplay();
		if (observer) observer.disconnect();
	});
</script>

<slot name="header"></slot>
<svelte:element
	this={tag}
	bind:this={$element}
	class={classNames('rf-carrousel', `transition-${animationType}`)}
	style={cssVariables({
		'--rf-carrousel-slide-width': singleElementWidth + 'px',
		'--rf-carrousel-current-index': currentIndex,
		'--rf-carrousel-default-space-between-items': `${spaceBetweenItems}px`,
		'--rf-carrousel-default-slides-per-view': `${animationType === 'fade' ? 1 : itemsPerView}`,
	})}
	role="slider"
	aria-valuemin="0"
	aria-valuemax={items.length}
	aria-valuenow={currentIndex}
	aria-valuetext={`Slide ${currentIndex} of ${items.length}`}
	tabindex="0"
	aria-orientation="horizontal"
	ontouchstart={handleTouchStart}
	ontouchend={handleTouchEnd}
	part="rf-carrousel"
>
	<div class="rf-carrousel__container">
		{#if pages > 1 && hasControls}
			<div class="rf-carrousel__controls" part="rf-carrousel__controls" role="group" aria-label="Carousel navigation">
				<button
					class="rf-carrousel__control rf-carrousel__control__prev"
					part="rf-carrousel__control__prev"
					aria-label="Previous slide"
					disabled={isAtStart && !loop}
					onclick={throttledToPreviousPage}
				>
					{previousLabel}
				</button>
				<button
					class="rf-carrousel__control rf-carrousel__control__next"
					part="rf-carrousel__control__next"
					aria-label="Next slide"
					disabled={isAtEnd && !loop}
					onclick={throttledToNextPage}
				>
					{nextLabel}
				</button>
			</div>
		{/if}
		<div aria-live="polite" class="sr-only">
			Slide {currentIndex + 1} of {items.length}
		</div>
		<div class="rf-carrousel__wrapper" part="rf-carrousel__wrapper"><slot></slot></div>
	</div>
	{#if hasBullets && pages > 0}
		<div class="rf-carrousel__bullets" part="rf-carrousel__bullets">
			{#each Array(pages) as _, i}
				<button class="rf-carrousel__bullet" part="rf-carrousel__bullet" class:active={currentIndex === i} onclick={() => setPage(i)}>
					{i + 1}
				</button>
			{/each}
		</div>
	{/if}
</svelte:element>
<slot name="footer"></slot>

<style lang="scss" global>
	.rf-carrousel {
		--rf-carrousel-transition-duration: 500ms;
		--rf-carrousel-transition-timing-function: ease;
		--rf-carrousel-space-between-items: var(--rf-carrousel-default-space-between-items, 20px);
		--rf-carrousel-slides-per-view: var(--rf-carrousel-default-slides-per-view, 3);
		--rf-carrousel-height: 512px;
		position: relative;
		overflow: hidden;
		touch-action: pan-y;
		&__container {
			position: relative;
			height: var(--rf-carrousel-height);
		}
		&__wrapper {
			display: flex;
			flex-direction: row;
			gap: var(--rf-carrousel-space-between-items);
			overflow: hidden;
			height: 100%;
			::slotted(rf-carrousel-slide) {
				width: var(--rf-carrousel-slide-width);
				flex-grow: 0;
				flex-shrink: 0;
			}

			.transition-slide & {
				::slotted(rf-carrousel-slide) {
					transition: transform var(--rf-carrousel-transition-duration) var(--rf-carrousel-transition-timing-function);
					transform: translateX(
						calc(
							var(--rf-carrousel-current-index) * var(--rf-carrousel-slide-width) * -1 - var(--rf-carrousel-space-between-items) *
								var(--rf-carrousel-current-index)
						)
					);
				}
			}
			.transition-fade & {
				height: 100%;
				::slotted(rf-carrousel-slide) {
					opacity: 0;
					transition: opacity var(--rf-carrousel-transition-duration) var(--rf-carrousel-transition-timing-function);
					transform: translateX(
						calc(
							var(--rf-carrousel-current-index) * var(--rf-carrousel-slide-width) * -1 - var(--rf-carrousel-space-between-items) *
								var(--rf-carrousel-current-index)
						)
					);
				}

				::slotted(rf-carrousel-slide.rf-carrousel-slide--in-view) {
					opacity: 1;
				}
			}
			.transition-page & {
				height: 100%;
				--rf-canvas-height: var(--rf-carrousel-height);
				::slotted(rf-carrousel-slide) {
					opacity: 0;
					transition: clip-path var(--rf-carrousel-transition-duration) var(--rf-carrousel-transition-timing-function);
					position: absolute;
				}

				::slotted(rf-carrousel-slide.rf-carrousel-slide--in-view) {
					clip-path: inset(0 0 0 0);
					opacity: 1;
					position: relative;
				}
				::slotted(rf-carrousel-slide.rf-carrousel-slide--previous) {
					opacity: 1;
					clip-path: inset(0 100% 0 0);
				}
				::slotted(rf-carrousel-slide.rf-carrousel-slide--next) {
					opacity: 1;
					clip-path: inset(0 0 0 100%);
				}
			}
		}

		.rf-carrousel__controls {
			display: flex;
			justify-content: space-between;
			position: absolute;
			top: 50%;
			width: 100%;
			transform: translateY(-50%);
			z-index: 1;
			box-sizing: border-box;
			padding: var(--rf-spacing-s, 8px);
		}

		.rf-carrousel__control {
			all: unset;
			width: var(--rf-spacing-xl, 40px);
			height: var(--rf-spacing-xl, 40px);
			color: transparent;
			background: var(--rf-main-background-color, white);
			cursor: pointer;
			transition: opacity 0.5s ease;
			border-radius: 50%;
			position: relative;
			&[disabled] {
				opacity: 0.5;
				cursor: not-allowed;
				&:hover {
					opacity: 0.5;
				}
			}
			&:hover {
				opacity: 0.8;
			}
			&::before {
				content: '←';
				display: flex;
				justify-content: center;
				align-items: center;
				color: var(--rf-primary-font-color, black);
				line-height: calc(var(--rf-spacing-xl, 40px) - 1px);
			}
			&__next {
				&::before {
					content: '→';
				}
			}
		}

		.rf-carrousel__bullets {
			position: absolute;
			display: flex;
			justify-content: center;
			box-sizing: border-box;
			gap: var(--rf-spacing-s, 8px);
			width: auto;
			padding: var(--rf-spacing-s, 8px);
			bottom: 0;
			left: 50%;
			transform: translateX(-50%);
		}

		.rf-carrousel__bullet {
			all: unset;
			display: block;
			background-color: var(--rf-main-background-color, white);
			opacity: 0.5;
			border: none;
			cursor: pointer;
			text-indent: -9999px;
			width: calc(var(--rf-spacing-xs, 4px) * 2);
			height: calc(var(--rf-spacing-xs, 4px) * 2);
			border-radius: 50%;
			margin: 0;
			transition: opacity 0.5s ease;
			&:hover {
				opacity: 0.8;
			}
			&.active {
				opacity: 1;
			}
		}

		.sr-only {
			position: absolute;
			left: -10000px;
			top: auto;
			width: 1px;
			height: 1px;
			overflow: hidden;
		}
	}
</style>
